import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CostBox from "@/components/jammable/CostBox";
import KeyMissing from "@/components/jammable/KeyMissing";
import MyVoices from "@/components/jammable/MyVoices";
import VoiceSearch from "@/components/jammable/VoiceSearch";
import { addMyVoice, estimateCost, getJammableStatus, money, myVoices, removeMyVoice, startRevoice } from "@/lib/jammableApi";
import { safeApiError } from "@/lib/safeErrorText";

const PITCHES = Array.from({ length: 25 }, (_, i) => i - 12);
const pitchLabel = (p) => (p === 0 ? "Same key" : p === 12 ? "+12 · an octave up (e.g. a man's part for a woman's voice)" : p === -12 ? "−12 · an octave down" : `${p > 0 ? "+" : "−"}${Math.abs(p)} semitone${Math.abs(p) > 1 ? "s" : ""}`);

/**
 * Change the voice with Jammable. `source` = { kind: "song", song_id } or { kind: "take", session_id, track_id, take_id },
 * plus { label, duration } for the price. Nothing is charged until you press the button.
 */
export default function RevoiceDialog({ open, onClose, source, onStarted }) {
  const [status, setStatus] = useState(null);
  const [mine, setMine] = useState([]);
  const [tab, setTab] = useState("mine");
  const [voice, setVoice] = useState(null);
  const [pitch, setPitch] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    getJammableStatus().then(setStatus).catch((e) => setStatus({ error: safeApiError(e, "Couldn't reach the studio") }));
    myVoices().then((r) => { setMine(r.voices); if (!r.voices.length) setTab("find"); }).catch(() => {});
  }, [open]);
  const saveVoice = async (v) => {
    try { const row = await addMyVoice(v.id); setMine((m) => [row, ...m.filter((x) => x.id !== row.id)]); toast.success(`${row.name} saved to My voices`); }
    catch (e) { toast.error("Couldn't save that voice", { description: safeApiError(e) }); }
  };
  const dropVoice = async (v) => {
    try { await removeMyVoice(v.id); setMine((m) => m.filter((x) => x.id !== v.id)); if (voice?.id === v.id) setVoice(null); }
    catch (e) { toast.error("Couldn't remove that voice", { description: safeApiError(e) }); }
  };
  const est = estimateCost(source.duration);
  const low = status?.balance != null && status.balance < est;
  const start = async () => {
    setBusy(true);
    try {
      const { label: _label, duration: _duration, ...src } = source;
      const job = await startRevoice({ ...src, voice_id: voice.id, voice_name: voice.name, voice_image: voice.image_url || null, pitch });
      toast.success("Voice change started", { description: "It usually takes 1–3 minutes — you can keep working." });
      onStarted?.(job);
      onClose();
    } catch (e) { toast.error("Couldn't start the voice change", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="jam-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Wand2 className="h-4 w-4 text-fuchsia-200" /> Change the voice</DialogTitle>
          <DialogDescription data-testid="jam-source" className="text-white/60">{source.label} · sung again in the voice you pick (Jammable)</DialogDescription>
        </DialogHeader>
        {!status ? <p className="flex items-center gap-2 text-[13px] text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Checking Jammable…</p>
          : status.error ? <p data-testid="jam-status-error" className="text-[13px] text-rose-300">{status.error}</p>
          : !status.configured ? <KeyMissing /> : (
            <div className="space-y-4">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-2 bg-white/[0.05]">
                  <TabsTrigger value="mine" data-testid="jam-tab-mine">My voices{mine.length ? ` (${mine.length})` : ""}</TabsTrigger>
                  <TabsTrigger value="find" data-testid="jam-tab-find">Find a voice</TabsTrigger>
                </TabsList>
                <TabsContent value="mine"><MyVoices voices={mine} selected={voice} onPick={setVoice} onRemove={dropVoice} onAdded={(v) => { setMine((m) => [v, ...m.filter((x) => x.id !== v.id)]); setVoice(v); }} /></TabsContent>
                <TabsContent value="find" forceMount className="data-[state=inactive]:hidden"><VoiceSearch categories={status.categories || []} selected={voice} onPick={setVoice} onSave={saveVoice} savedIds={new Set(mine.map((v) => v.id))} /></TabsContent>
              </Tabs>
              <label className="flex flex-wrap items-center gap-2 text-[13px] text-white/75">Key change
                <select data-testid="jam-pitch" value={pitch} onChange={(e) => setPitch(Number(e.target.value))} className="w-full min-w-0 max-w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:outline-none sm:w-auto">
                  {PITCHES.map((p) => <option key={p} value={p}>{pitchLabel(p)}</option>)}
                </select>
              </label>
              <CostBox duration={source.duration} label={source.label} status={status} />
              <p className="text-[11px] text-white/40">Only use songs and voices you have the rights to.</p>
              <button type="button" data-testid="jam-start" onClick={start} disabled={!voice || busy || low}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-violet-600 py-3 font-display font-semibold transition-transform duration-150 active:scale-[0.98] disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {voice ? `Sing it as ${voice.name} — about ${money(est)}` : "Pick a voice first"}
              </button>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
